import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Nav — renders a semantic <nav> landmark with an enforced aria-label.
 *
 * Every navigation region must be labelled so screen reader users who navigate
 * by landmark can distinguish between them (WCAG 2.4.1, ARIA landmark best practice).
 *
 * Provide a short, descriptive label that identifies the navigation's purpose:
 * "Main", "Footer", "Breadcrumb", "Social", etc.
 *
 * @atomic-tier Atom
 * @category Semantic
 */

export interface NavProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * A short descriptive label for the navigation region.
   * Required for WCAG 2.4.1 compliance when multiple <nav> elements are present on a page.
   * Examples: "Main", "Footer", "Breadcrumb", "Social links"
   */
  'aria-label'?: string
  'aria-labelledby'?: string
}

const Nav = React.forwardRef<HTMLElement, NavProps>(
  ({ className, children, ...props }, ref) => (
    <nav ref={ref} role="navigation" className={cn(className)} {...props}>
      {children}
    </nav>
  ),
)
Nav.displayName = "Nav"

export { Nav }
